import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { parseISO, format, isValid } from "date-fns";
import { FaPencilAlt, FaFileExcel, FaCheckDouble, FaRegFileAlt, FaRegClock, FaExclamationTriangle, FaRegCheckCircle, FaFileUpload, FaEraser } from "react-icons/fa";
import RequestService from "../services/RequestService";
import Movimentacao36Service from "../services/Movimentacao36Service";
import JustificarModal from "./modals/JustificarModal";
import ValidarJustificativaModal from "./modals/ValidarJustificativaModal";
import RequestDetailsModal from "./modals/RequestDetailsModal";
import exportToExcelPortalJustificativas from "../utils/exportToExcelPortalJustificativas";
import StatusJustificativa from "../enums/StatusJustificativa";
import MotivoJustificativa from "../enums/MotivoJustificativa";
import ResponsavelJustificativa from "../enums/ResponsavelJustificativa";
import { Context } from "../context/UserContext";

// O backend decide quais chamados precisam de justificativa (filtro unificado:
// abertos no mês + perdeu SLA / FINALIZADO que passou por gestão / DRS atendido
// diferente do indicado). Cada chamado vem com `regras[]` indicando o porquê.
const ITEMS_PER_PAGE = 15;

// 'YYYY-MM-DD' no fuso local (evita o deslocamento de toISOString).
function toInputDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// Período padrão ao abrir a tela: mês corrente (do dia 1 ao último dia).
const HOJE = new Date();
const PERIODO_PADRAO = {
    inicio: toInputDate(new Date(HOJE.getFullYear(), HOJE.getMonth(), 1)),
    fim: toInputDate(new Date(HOJE.getFullYear(), HOJE.getMonth() + 1, 0)),
};

// Rótulo a partir do código salvo — a grade mostra o que a pessoa escolheu
// ('Falha operacional'), não o código ('falha-operacional').
const rotuloPor = (lista) => (cod) =>
    !cod ? "" : (lista.find((item) => item.cod === cod)?.descricao || cod);
const rotuloMotivo = rotuloPor(MotivoJustificativa);
const rotuloResponsavel = rotuloPor(ResponsavelJustificativa);

// Chamados BBN não são justificados (SLA próprio). Por padrão ficam fora da
// fila; o filtro permite trazê-los para conferência.
const FILTROS_BBN = [
    { cod: "ocultar", descricao: "Ocultar BBN" },
    { cod: "todos", descricao: "Incluir BBN" },
    { cod: "somente", descricao: "Somente BBN" },
];

// Card de aging (0-1 / 2-3 / 4+ dias)
function AgingBox({ label, value }) {
    return (
        <div className="flex flex-col items-center justify-center bg-white/15 rounded-lg py-2 flex-1">
            <span className="text-[9px] font-semibold tracking-wide text-white/70 uppercase">{label}</span>
            <span className="text-lg font-bold leading-none mt-1">{value ?? 0}</span>
        </div>
    );
}

export default function PortalJustificativas() {
    const { user } = useContext(Context);
    // Vivo só valida/recusa; Amerinode justifica/replica.
    const isVivo = user?.role === "vivogestao";

    // Filtro por status do workflow ('' = todos os status).
    const [statusFiltro, setStatusFiltro] = useState("");
    // Período de abertura dos chamados ('YYYY-MM-DD') — começa no mês corrente.
    const [dataInicio, setDataInicio] = useState(PERIODO_PADRAO.inicio);
    const [dataFim, setDataFim] = useState(PERIODO_PADRAO.fim);
    // Busca por identificador do chamado (parcial). `idRequestInput` é o que o
    // usuário digita; `idRequestFiltro` é o valor debounced que vai para a query,
    // evitando uma requisição por tecla.
    const [idRequestInput, setIdRequestInput] = useState("");
    const [idRequestFiltro, setIdRequestFiltro] = useState("");
    // Período de ENTREGA (dataHoraRecebimento) — vazio = sem filtro.
    const [dataEntregaInicio, setDataEntregaInicio] = useState("");
    const [dataEntregaFim, setDataEntregaFim] = useState("");
    // BBN: fora da fila por padrão (ver FILTROS_BBN).
    const [bbnFiltro, setBbnFiltro] = useState("ocultar");
    const [requests, setRequests] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRequests, setTotalRequests] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    // Resumo (cards de dashboard)
    const [summary, setSummary] = useState(null);

    // Modal Justificar (Amerinode)
    const [modalOpen, setModalOpen] = useState(false);
    const [requestSelecionada, setRequestSelecionada] = useState(null);

    // Modal Validar (Vivo)
    const [validarModalOpen, setValidarModalOpen] = useState(false);
    const [requestParaValidar, setRequestParaValidar] = useState(null);

    // Modal Detalhes da Request
    const [requestDetailsModal, setRequestDetailsModal] = useState(false);
    const [requestOnDetailsModal, setRequestOnDetailsModal] = useState();

    const [exporting, setExporting] = useState(false);

    // Upload da planilha de movimentações (36 meses)
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [lastUpload, setLastUpload] = useState(null);

    // A tela só consulta quando há ao menos um filtro. Sem nenhum (após "Limpar"),
    // fica em branco até o usuário escolher um critério.
    const temFiltro = Boolean(
        dataInicio || dataFim || dataEntregaInicio || dataEntregaFim ||
        idRequestFiltro.trim() || statusFiltro
    );

    const fetchRequests = useCallback(async (page = 1) => {
        if (!temFiltro) {
            setRequests([]);
            setTotalPages(1);
            setTotalRequests(0);
            setCurrentPage(1);
            return;
        }
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFiltro) params.append("statusjustificativa", statusFiltro);
            if (dataInicio) params.append("dataInicio", dataInicio);
            if (dataFim) params.append("dataFim", dataFim);
            if (idRequestFiltro.trim()) params.append("idrequest", idRequestFiltro.trim());
            if (dataEntregaInicio) params.append("dataEntregaInicio", dataEntregaInicio);
            if (dataEntregaFim) params.append("dataEntregaFim", dataEntregaFim);
            if (bbnFiltro) params.append("bbn", bbnFiltro);
            const qs = params.toString();
            const data = await RequestService.getRequestsJustificativas(qs ? `?${qs}` : "", page, ITEMS_PER_PAGE);
            if (data) {
                setRequests(data.requests || []);
                setTotalPages(data.totalPages || 1);
                setTotalRequests(data.totalRequests || 0);
                setCurrentPage(page);
            }
        } catch (error) {
            toast.error(error?.message || "Erro ao carregar relatório de justificativas.");
        } finally {
            setLoading(false);
        }
    }, [temFiltro, statusFiltro, dataInicio, dataFim, idRequestFiltro, dataEntregaInicio, dataEntregaFim, bbnFiltro]);

    const fetchSummary = useCallback(async () => {
        if (!temFiltro) { setSummary(null); return; }
        try {
            const params = new URLSearchParams();
            if (dataInicio) params.append("dataInicio", dataInicio);
            if (dataFim) params.append("dataFim", dataFim);
            if (idRequestFiltro.trim()) params.append("idrequest", idRequestFiltro.trim());
            if (dataEntregaInicio) params.append("dataEntregaInicio", dataEntregaInicio);
            if (dataEntregaFim) params.append("dataEntregaFim", dataEntregaFim);
            if (bbnFiltro) params.append("bbn", bbnFiltro);
            const qs = params.toString();
            const data = await RequestService.summaryJustificativas(qs ? `?${qs}` : "");
            setSummary(data);
        } catch (error) {
            // resumo é complementar — não bloqueia a tela
            console.error("Erro ao carregar resumo:", error);
        }
    }, [temFiltro, dataInicio, dataFim, idRequestFiltro, dataEntregaInicio, dataEntregaFim, bbnFiltro]);

    const fetchLastUpload = useCallback(async () => {
        try {
            const data = await Movimentacao36Service.getLastUpload();
            setLastUpload(data?.lastUpload || null);
        } catch (error) {
            // informativo — não bloqueia a tela
            console.error("Erro ao buscar último upload:", error);
        }
    }, []);

    // Debounce da busca por ID (500ms).
    useEffect(() => {
        const t = setTimeout(() => setIdRequestFiltro(idRequestInput), 500);
        return () => clearTimeout(t);
    }, [idRequestInput]);

    useEffect(() => {
        fetchRequests(1);
    }, [fetchRequests]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    useEffect(() => {
        fetchLastUpload();
    }, [fetchLastUpload]);

    const handleUploadPlanilha = async (e) => {
        const file = e.target.files?.[0];
        // Limpa o input para permitir reenviar o mesmo arquivo depois.
        e.target.value = "";
        if (!file) return;

        setUploading(true);
        try {
            const resp = await Movimentacao36Service.uploadPlanilha(file);
            toast.success(resp?.message || "Planilha importada com sucesso!");
            fetchLastUpload();
        } catch (error) {
            toast.error(error?.message || "Erro ao enviar a planilha.");
        } finally {
            setUploading(false);
        }
    };

    // "Limpar" zera TODOS os filtros, inclusive o período de abertura que vem
    // pré-preenchido. Sem nenhum filtro a tela não consulta nada — fica em branco
    // até o usuário escolher um novo critério.
    const handleLimparFiltros = () => {
        setStatusFiltro("");
        setIdRequestInput("");
        setIdRequestFiltro("");
        setDataEntregaInicio("");
        setDataEntregaFim("");
        setDataInicio("");
        setDataFim("");
        setBbnFiltro("ocultar");
    };

    // Recarrega lista + resumo após uma ação de workflow.
    const reloadTudo = () => {
        fetchRequests(currentPage);
        fetchSummary();
    };

    const handleAbrirModal = (request) => {
        setRequestSelecionada(request);
        setModalOpen(true);
    };

    const handleAbrirValidar = (request) => {
        setRequestParaValidar(request);
        setValidarModalOpen(true);
    };

    const handleRequestDetailsModal = (ticketId) => {
        setRequestDetailsModal(true);
        setRequestOnDetailsModal(ticketId);
    };

    const handleExportToExcel = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (statusFiltro) params.append("statusjustificativa", statusFiltro);
            if (dataInicio) params.append("dataInicio", dataInicio);
            if (dataFim) params.append("dataFim", dataFim);
            if (idRequestFiltro.trim()) params.append("idrequest", idRequestFiltro.trim());
            if (dataEntregaInicio) params.append("dataEntregaInicio", dataEntregaInicio);
            if (dataEntregaFim) params.append("dataEntregaFim", dataEntregaFim);
            if (bbnFiltro) params.append("bbn", bbnFiltro);
            const qs = params.toString();
            const data = await RequestService.getRequestsJustificativas(qs ? `?${qs}` : "", 1, 0, true);
            const lista = data?.requests || [];
            if (lista.length === 0) {
                toast.info("Nenhum registro para exportar.");
                return;
            }
            await exportToExcelPortalJustificativas(lista);
            toast.success(`${lista.length} registros exportados.`);
        } catch (error) {
            toast.error(error?.message || "Erro ao exportar para Excel.");
        } finally {
            setExporting(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            fetchRequests(newPage);
        }
    };

    const formatDateSafe = (dateValue) => {
        if (!dateValue) return "—";
        try {
            const d = typeof dateValue === "string" ? parseISO(dateValue.trim()) : new Date(dateValue);
            return isValid(d) ? format(d, "dd/MM/yy HH:mm") : "—";
        } catch {
            return "—";
        }
    };

    const getStatusJustif = (req) => req?.justificativaInfo?.status || "JUSTIFICAR";

    // 'YYYY-MM-DD' → 'DD/MM/AAAA' (sem passar por Date, evita deslocamento de fuso).
    const formatDateOnly = (d) => (d ? d.split("-").reverse().join("/") : "");

    // Rótulo curto da faixa para a grade. '99' = Dentro de Área.
    const faixaLabel = (cod) => (!cod ? "—" : cod === "99" ? "Dentro Área" : `Faixa ${cod}`);

    // Só é permitida UMA réplica — se já houve um REPLICOU no histórico, não pode replicar de novo.
    const jaReplicou = (req) => (req?.justificativaInfo?.historico || []).some((h) => h.acao === "REPLICOU");

    // Clicar num card aplica o filtro de status correspondente.
    const filtrarPorCard = (status) => setStatusFiltro(status);

    const inputLight = "block rounded-lg px-3 py-2 bg-white text-slate-900 border border-slate-300 outline-none focus:border-sky-500";

    return (
        <div className="flex flex-col w-full h-full overflow-y-auto bg-slate-100 text-slate-800 p-4 gap-4">

            {/* Cabeçalho */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[11px] tracking-[0.2em] font-semibold text-slate-400 uppercase">Portal Operacional</div>
                        <h1 className="text-3xl font-bold mt-1 text-slate-900">Portal de Justificativas</h1>
                    </div>

                    {/* Upload da planilha de movimentações (36 meses) — só Amerinode */}
                    {!isVivo && (
                        <div className="flex flex-col items-end gap-1">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xlsm"
                                onChange={handleUploadPlanilha}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-[12px] font-semibold"
                            >
                                <FaFileUpload />
                                {uploading ? "Enviando..." : "Enviar Planilha de Movimentações"}
                            </button>
                            <div className="text-[11px] text-slate-500 text-right">
                                {lastUpload ? (
                                    <>
                                        Último upload: <span className="font-semibold">{formatDateSafe(lastUpload.uploadedAt)}</span>
                                        {lastUpload.userName ? ` por ${lastUpload.userName}` : ""}
                                        {typeof lastUpload.totalItens === "number" ? ` · ${lastUpload.totalItens} cód. SAP` : ""}
                                    </>
                                ) : (
                                    "Nenhuma planilha enviada ainda."
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Cards de resumo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

                {/* Aguardando Justificativa */}
                <button
                    onClick={() => filtrarPorCard("JUSTIFICAR")}
                    className={`text-left rounded-2xl p-5 bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-lg transition ring-offset-2 ring-offset-slate-100 ${statusFiltro === "JUSTIFICAR" ? "ring-2 ring-blue-500" : "hover:brightness-110"}`}
                >
                    <div className="flex items-start justify-between">
                        <span className="text-[11px] font-semibold tracking-wide uppercase leading-tight">Aguardando<br />Justificativa</span>
                        <FaRegFileAlt className="opacity-80" />
                    </div>
                    <div className="flex items-end gap-2 mt-3">
                        <span className="text-4xl font-bold leading-none">{summary?.aguardandoJustificativa?.total ?? 0}</span>
                        <span className="text-xs opacity-80 mb-1">itens</span>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <AgingBox label="0-1 dia" value={summary?.aguardandoJustificativa?.b01} />
                        <AgingBox label="2-3 dias" value={summary?.aguardandoJustificativa?.b23} />
                        <AgingBox label="4+ dias" value={summary?.aguardandoJustificativa?.b4} />
                    </div>
                </button>

                {/* Aguardando Aprovação */}
                <button
                    onClick={() => filtrarPorCard("AGUARDANDO_APROVACAO")}
                    className={`text-left rounded-2xl p-5 bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg transition ring-offset-2 ring-offset-slate-100 ${statusFiltro === "AGUARDANDO_APROVACAO" ? "ring-2 ring-emerald-500" : "hover:brightness-110"}`}
                >
                    <div className="flex items-start justify-between">
                        <span className="text-[11px] font-semibold tracking-wide uppercase leading-tight">Aguardando<br />Aprovação</span>
                        <FaRegClock className="opacity-80" />
                    </div>
                    <div className="flex items-end gap-2 mt-3">
                        <span className="text-4xl font-bold leading-none">{summary?.aguardandoAprovacao?.total ?? 0}</span>
                        <span className="text-xs opacity-80 mb-1">itens</span>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <AgingBox label="0-1 dia" value={summary?.aguardandoAprovacao?.b01} />
                        <AgingBox label="2-3 dias" value={summary?.aguardandoAprovacao?.b23} />
                        <AgingBox label="4+ dias" value={summary?.aguardandoAprovacao?.b4} />
                    </div>
                </button>

                {/* Recusados */}
                <button
                    onClick={() => filtrarPorCard("RECUSADO")}
                    className={`text-left rounded-2xl p-5 bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-lg transition ring-offset-2 ring-offset-slate-100 ${statusFiltro === "RECUSADO" ? "ring-2 ring-orange-500" : "hover:brightness-110"}`}
                >
                    <div className="flex items-start justify-between">
                        <span className="text-[11px] font-semibold tracking-wide uppercase">Recusados</span>
                        <FaExclamationTriangle className="opacity-80" />
                    </div>
                    <div className="flex items-end gap-2 mt-3">
                        <span className="text-4xl font-bold leading-none">{summary?.recusados?.total ?? 0}</span>
                        <span className="text-xs opacity-80 mb-1">itens</span>
                    </div>
                    <div className="mt-4 bg-white/15 rounded-lg py-2 text-center text-[11px] font-semibold uppercase tracking-wide">
                        Visão geral
                    </div>
                </button>

                {/* Total de Chamados */}
                <button
                    onClick={() => filtrarPorCard("")}
                    className={`text-left rounded-2xl p-5 bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-lg transition ring-offset-2 ring-offset-slate-100 ${statusFiltro === "" ? "ring-2 ring-slate-500" : "hover:brightness-110"}`}
                >
                    <div className="flex items-start justify-between">
                        <span className="text-[11px] font-semibold tracking-wide uppercase">Total de Chamados</span>
                        <FaRegCheckCircle className="opacity-80" />
                    </div>
                    <div className="flex items-end gap-2 mt-3">
                        <span className="text-4xl font-bold leading-none">{summary?.totalChamados ?? 0}</span>
                        <span className="text-xs opacity-80 mb-1">itens</span>
                    </div>
                    <div className="mt-4 bg-white/10 rounded-lg py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                        Visão geral
                    </div>
                </button>
            </div>

            {/* Filtros */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
                <div className="flex items-end justify-between gap-3 flex-wrap text-[12px]">
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Aberto de</label>
                            <input
                                type="date"
                                value={dataInicio}
                                max={dataFim || undefined}
                                onChange={(e) => setDataInicio(e.target.value)}
                                className={inputLight}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">até</label>
                            <input
                                type="date"
                                value={dataFim}
                                min={dataInicio || undefined}
                                onChange={(e) => setDataFim(e.target.value)}
                                className={inputLight}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Entregue de</label>
                            <input
                                type="date"
                                value={dataEntregaInicio}
                                max={dataEntregaFim || undefined}
                                onChange={(e) => setDataEntregaInicio(e.target.value)}
                                className={inputLight}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">até</label>
                            <input
                                type="date"
                                value={dataEntregaFim}
                                min={dataEntregaInicio || undefined}
                                onChange={(e) => setDataEntregaFim(e.target.value)}
                                className={inputLight}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Nº Solicitação</label>
                            <input
                                type="text"
                                placeholder="Buscar por ID"
                                value={idRequestInput}
                                onChange={(e) => setIdRequestInput(e.target.value)}
                                className={inputLight}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Status</label>
                            <select value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)} className={inputLight}>
                                <option value="">Todos</option>
                                {Object.entries(StatusJustificativa).map(([key, { descricao }]) => (
                                    <option key={key} value={key}>{descricao}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">BBN</label>
                            <select
                                value={bbnFiltro}
                                onChange={(e) => setBbnFiltro(e.target.value)}
                                title="Chamados BBN têm SLA próprio e não são justificados"
                                className={inputLight}
                            >
                                {FILTROS_BBN.map((f) => (
                                    <option key={f.cod} value={f.cod}>{f.descricao}</option>
                                ))}
                            </select>
                        </div>

                    <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide invisible">Ações</label>
                            <button
                                onClick={handleLimparFiltros}
                                disabled={!temFiltro}
                                title="Voltar os filtros ao padrão"
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${temFiltro ? "border-slate-300 text-slate-700 hover:bg-slate-50" : "border-slate-200 text-slate-300 cursor-not-allowed"}`}
                            >
                                <FaEraser />
                                Limpar
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={handleExportToExcel}
                        disabled={exporting || loading || !temFiltro}
                        className="flex justify-center items-center bg-green-600 hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed px-3 py-2 rounded-lg text-white gap-2"
                    >
                        <FaFileExcel />
                        {exporting ? "Exportando..." : "Exportar Excel"}
                    </button>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                    {!temFiltro ? (
                        <>Nenhum filtro aplicado — selecione um período, nº de solicitação ou status para exibir os chamados.</>
                    ) : (<>
                    {/* Espelha a regra do backend: sem período de abertura, o padrão
                        é "mês corrente" — EXCETO quando há período de entrega, que
                        aí vira o único recorte. Dizer "mês corrente" nesse caso
                        seria descrever um filtro que não está sendo aplicado. */}
                    {(dataInicio || dataFim || !(dataEntregaInicio || dataEntregaFim)) ? (<>
                    Chamados FINALIZADOS abertos
                    {dataInicio && dataFim
                        ? <> entre <span className="font-semibold">{formatDateOnly(dataInicio)}</span> e <span className="font-semibold">{formatDateOnly(dataFim)}</span></>
                        : dataInicio
                            ? <> a partir de <span className="font-semibold">{formatDateOnly(dataInicio)}</span></>
                            : dataFim
                                ? <> até <span className="font-semibold">{formatDateOnly(dataFim)}</span></>
                                : <> no mês corrente</>}
                    </>) : (<>
                    Chamados FINALIZADOS entregues
                    {dataEntregaInicio && dataEntregaFim
                        ? <> entre <span className="font-semibold">{formatDateOnly(dataEntregaInicio)}</span> e <span className="font-semibold">{formatDateOnly(dataEntregaFim)}</span></>
                        : dataEntregaInicio
                            ? <> a partir de <span className="font-semibold">{formatDateOnly(dataEntregaInicio)}</span></>
                            : <> até <span className="font-semibold">{formatDateOnly(dataEntregaFim)}</span></>}
                    {" "}(abertos em qualquer mês)
                    </>)}
                    {" "}que precisam de justificativa (SLA perdido, passou por gestão, ou DRS atendido diferente do indicado).
                    {idRequestFiltro.trim() && (
                        <span className="block mt-1 text-slate-600">
                            Busca por nº de solicitação <span className="font-semibold">ignora o período de abertura</span> — procura em todos os meses.
                        </span>
                    )}
                    </>)}
                </div>
            </div>

            {/* Tabela */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-slate-700">Registros filtrados</div>
                    <div className="text-xs text-slate-500">{totalRequests} itens encontrados</div>
                </div>

                <div className="overflow-x-auto rounded-lg">
                    <table className="min-w-full text-[11px]">
                        <thead className="bg-slate-800 text-white">
                            <tr>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">ID Solicitação</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">Status</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">Status Justif.</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">DRS Original</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">DRS Atendido</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">Criada em</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">SLA</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">Entregue em</th>
                                <th className="py-2 px-3 text-center font-semibold uppercase tracking-wide">Faixa</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">Regra</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">Motivo</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">Responsável</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">Justificativa</th>
                                <th className="py-2 px-3 text-left font-semibold uppercase tracking-wide">Obs. Vivo</th>
                                <th className="py-2 px-3 text-center font-semibold uppercase tracking-wide">Somente Fat.</th>
                                <th className="py-2 px-3 text-center font-semibold uppercase tracking-wide">Ação</th>
                            </tr>
                        </thead>
                        <tbody className="text-slate-700">
                            {loading && (
                                <tr><td colSpan={16} className="text-center py-6 text-slate-400">Carregando...</td></tr>
                            )}
                            {!loading && requests.length === 0 && (
                                <tr><td colSpan={16} className="text-center py-6 text-slate-400">
                                    {temFiltro
                                        ? "Nenhuma solicitação se enquadra no filtro atual."
                                        : "Selecione um filtro para exibir os chamados."}
                                </td></tr>
                            )}
                            {!loading && requests.map((req) => {
                                const statusJustif = getStatusJustif(req);
                                const statusMeta = StatusJustificativa[statusJustif];
                                return (
                                    <tr key={req._id} className="border-b border-slate-200 hover:bg-slate-50">
                                        <td className="py-1.5 px-3 whitespace-nowrap">
                                            <span
                                                className="cursor-pointer underline font-bold text-sky-600"
                                                onClick={() => handleRequestDetailsModal(req._id)}
                                            >
                                                {req.idRequest}
                                            </span>
                                            {req.ehBbn && (
                                                <span
                                                    title="Atendimento BBN — SLA próprio, não entra na fila de justificativas"
                                                    className="ml-1 inline-block px-1.5 py-0.5 rounded-full bg-yellow-300 text-yellow-900 text-[9px] font-bold align-middle"
                                                >
                                                    BBN
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-1.5 px-3">{req.status}</td>
                                        <td className="py-1.5 px-3">
                                            <span
                                                className="inline-block px-2 py-0.5 rounded-full text-white text-[10px] font-semibold whitespace-nowrap"
                                                style={{ backgroundColor: statusMeta?.cor || "#6B7280" }}
                                            >
                                                {statusMeta?.descricao || statusJustif}
                                            </span>
                                        </td>
                                        <td className="py-1.5 px-3">{req.drsOriginal?.sigla || "—"}</td>
                                        <td className="py-1.5 px-3">{req.drsAtender?.sigla || "—"}</td>
                                        <td className="py-1.5 px-3 whitespace-nowrap">{formatDateSafe(req.createdAt)}</td>
                                        <td className="py-1.5 px-3 whitespace-nowrap text-red-600 font-semibold">{formatDateSafe(req.slaDate)}</td>
                                        <td className="py-1.5 px-3 whitespace-nowrap">{formatDateSafe(req.dataHoraRecebimento)}</td>
                                        <td className="py-1.5 px-3 text-center whitespace-nowrap">
                                            {(req.faixa_final || req.faixa) ? (
                                                <span
                                                    title={req.faixa_final && req.faixa && req.faixa_final !== req.faixa ? `Faixa original calculada: ${faixaLabel(req.faixa)}` : undefined}
                                                    className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${(req.faixa_final || req.faixa) === "99" ? "bg-slate-100 text-slate-600" : "bg-indigo-100 text-indigo-800"}`}
                                                >
                                                    {faixaLabel(req.faixa_final || req.faixa)}
                                                    {req.faixa_final && req.faixa && req.faixa_final !== req.faixa ? " *" : ""}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="py-1.5 px-3">
                                            <div className="flex flex-wrap gap-1">
                                                {(req.regras || []).length > 0 ? (
                                                    req.regras.map((rg) => (
                                                        <span key={rg} className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold whitespace-nowrap">
                                                            {rg}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </div>
                                        </td>
                                        {/* Motivo e Responsável: a Vivo já os via no modal de
                                            validação; quem preenche, não via em lugar nenhum
                                            depois de enviar. */}
                                        <td className="py-1.5 px-3 whitespace-nowrap">
                                            {req.justificativaInfo?.motivo ? (
                                                rotuloMotivo(req.justificativaInfo.motivo)
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="py-1.5 px-3 whitespace-nowrap">
                                            {req.justificativaInfo?.responsavel ? (
                                                rotuloResponsavel(req.justificativaInfo.responsavel)
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="py-1.5 px-3 max-w-xs">
                                            {req.justificativa && req.justificativa.trim() ? (
                                                <span title={req.justificativa} className="line-clamp-2">{req.justificativa}</span>
                                            ) : (
                                                <span className="text-slate-400 italic">— sem justificativa —</span>
                                            )}
                                        </td>
                                        <td className="py-1.5 px-3 max-w-xs">
                                            {req.justificativaInfo?.observacaoVivo && req.justificativaInfo.observacaoVivo.trim() ? (
                                                <span title={req.justificativaInfo.observacaoVivo} className="line-clamp-2">{req.justificativaInfo.observacaoVivo}</span>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="py-1.5 px-3 text-center">
                                            {req.justificativaInfo?.somenteFaturamento ? (
                                                <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold">Sim</span>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="py-1.5 px-3 text-center">
                                            {isVivo ? (
                                                statusJustif === "AGUARDANDO_APROVACAO" ? (
                                                    <button
                                                        onClick={() => handleAbrirValidar(req)}
                                                        className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-0.5 rounded-full text-[10px] mx-auto"
                                                    >
                                                        <FaCheckDouble size={10} />
                                                        Validar
                                                    </button>
                                                ) : (
                                                    <button onClick={() => handleRequestDetailsModal(req._id)} className="text-sky-600 hover:underline text-[10px]">Ver</button>
                                                )
                                            ) : (
                                                statusJustif === "JUSTIFICAR" ? (
                                                    <button
                                                        onClick={() => handleAbrirModal(req)}
                                                        className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-0.5 rounded-full text-[10px] mx-auto"
                                                    >
                                                        <FaPencilAlt size={10} />
                                                        Justificar
                                                    </button>
                                                ) : statusJustif === "AGUARDANDO_APROVACAO" ? (
                                                    /* Correção enquanto a Vivo não decidiu. Depois do
                                                       parecer dela o botão some e só resta "Ver". */
                                                    <button
                                                        onClick={() => handleAbrirModal(req)}
                                                        title="Corrigir enquanto a Vivo não avaliou"
                                                        className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-0.5 rounded-full text-[10px] mx-auto"
                                                    >
                                                        <FaPencilAlt size={10} />
                                                        Editar
                                                    </button>
                                                ) : ((statusJustif === "RECUSADO" || statusJustif === "EM_REPLICA") && !jaReplicou(req)) ? (
                                                    <button
                                                        onClick={() => handleAbrirModal(req)}
                                                        className="flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-3 py-0.5 rounded-full text-[10px] mx-auto"
                                                    >
                                                        <FaPencilAlt size={10} />
                                                        Replicar
                                                    </button>
                                                ) : (
                                                    /* Travado (abonado / não aceito / réplica gasta):
                                                       abre a própria justificativa em leitura, e não os
                                                       detalhes do chamado — o que ela quer conferir é o
                                                       que foi preenchido. */
                                                    <button
                                                        onClick={() => handleAbrirModal(req)}
                                                        className="text-sky-600 hover:underline text-[10px]"
                                                    >
                                                        Ver
                                                    </button>
                                                )
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                    <div className="flex justify-center items-center mt-4 gap-2 text-xs">
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            title="Primeira página"
                            className={`px-2 py-1 rounded-md ${currentPage === 1 ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-sky-600 text-white hover:bg-sky-700"}`}
                        >
                            {"<<"}
                        </button>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            title="Página anterior"
                            className={`px-2 py-1 rounded-md ${currentPage === 1 ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-sky-600 text-white hover:bg-sky-700"}`}
                        >
                            {"<"}
                        </button>
                        <span className="px-2 py-1 text-slate-500">Página {currentPage} de {totalPages}</span>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            title="Próxima página"
                            className={`px-2 py-1 rounded-md ${currentPage === totalPages ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-sky-600 text-white hover:bg-sky-700"}`}
                        >
                            {">"}
                        </button>
                        <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            title="Última página"
                            className={`px-2 py-1 rounded-md ${currentPage === totalPages ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-sky-600 text-white hover:bg-sky-700"}`}
                        >
                            {">>"}
                        </button>
                    </div>
                )}
            </div>

            <JustificarModal
                isOpen={modalOpen}
                setIsOpen={setModalOpen}
                request={requestSelecionada}
                onSaved={reloadTudo}
                // Abrir o modal pode persistir dados (write-through da faixa),
                // então recarrega o portal também ao fechar sem salvar.
                onClosed={reloadTudo}
            />

            <ValidarJustificativaModal
                isOpen={validarModalOpen}
                setIsOpen={setValidarModalOpen}
                request={requestParaValidar}
                onSaved={reloadTudo}
            />

            {requestOnDetailsModal && (
                <div className="absolute">
                    <RequestDetailsModal
                        isModalOpen={requestDetailsModal}
                        setModalOpen={() => { setRequestDetailsModal(!requestDetailsModal); }}
                        requestId={requestOnDetailsModal}
                    />
                </div>
            )}
        </div>
    );
}
